<template>
    <multiselect ref="multiselect" v-bind="$attrs" v-on="listeners" :options-limit="renderLimit">
        <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
            <slot :name="slotName" />
        </template>
    </multiselect>
</template>

<script>
const BASE_LIMIT = 1000;
const CHUNK_SIZE = 500;
const ROWS_BEFORE_BOTTOM = 100;

const MULTISELECT_LIST_REF = 'list';
const MULTISELECT_OPTION_SELECTOR = '.multiselect__element';

function warnMissingInternal(detail) {
    if (process.env.NODE_ENV !== 'production') {
        console.warn(`infinite-multiselect: ${detail}; infinite scroll disabled for this dropdown.`);
    }
}

export default {
    name: 'infinite-multiselect',
    inheritAttrs: false,
    data() {
        return {
            renderLimit: BASE_LIMIT,
            scrollWrapper: null,
            rowHeight: 0,
            scrollTicking: false,
            bufferTriggered: false,
        };
    },
    computed: {
        listeners() {
            return {
                ...this.$listeners,
                open: this.handleOpen,
                close: this.handleClose,
                'search-change': this.handleSearchChange,
            };
        },
    },
    beforeDestroy() {
        this.detachScrollListener();
    },
    methods: {
        handleOpen(id) {
            this.resetRenderLimit();
            this.$nextTick(this.attachScrollListener);
            this.$emit('open', id);
        },
        handleClose(value, id) {
            this.detachScrollListener();
            this.resetRenderLimit();
            this.$emit('close', value, id);
        },
        handleSearchChange(query, id) {
            this.resetRenderLimit();
            this.$emit('search-change', query, id);
        },
        resetRenderLimit() {
            this.renderLimit = BASE_LIMIT;
            this.rowHeight = 0;
            this.bufferTriggered = false;
        },
        attachScrollListener() {
            const wrapper = this.$refs.multiselect && this.$refs.multiselect.$refs[MULTISELECT_LIST_REF];
            if (!wrapper) {
                warnMissingInternal(`could not find internal ref "${MULTISELECT_LIST_REF}"`);
                return;
            }

            this.scrollWrapper = wrapper;
            wrapper.addEventListener('scroll', this.handleScroll, { passive: true });
        },
        detachScrollListener() {
            if (this.scrollWrapper) {
                this.scrollWrapper.removeEventListener('scroll', this.handleScroll);
                this.scrollWrapper = null;
            }
        },
        handleScroll() {
            if (this.scrollTicking) return;
            this.scrollTicking = true;

            requestAnimationFrame(() => {
                this.scrollTicking = false;
                this.checkScrollPosition();
            });
        },
        checkScrollPosition() {
            const wrapper = this.scrollWrapper;
            if (!wrapper) return;

            if (!this.rowHeight) {
                const row = wrapper.querySelector(MULTISELECT_OPTION_SELECTOR);
                if (!row) {
                    warnMissingInternal(`could not find option row "${MULTISELECT_OPTION_SELECTOR}"`);
                    return;
                }
                this.rowHeight = row.offsetHeight;
            }

            const buffer = this.rowHeight * ROWS_BEFORE_BOTTOM;
            const distanceToBottom = wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight;

            if (distanceToBottom > buffer) {
                this.bufferTriggered = false;
                return;
            }

            if (!this.bufferTriggered) {
                this.bufferTriggered = true;
                this.renderLimit += CHUNK_SIZE;
            }
        },
    },
};
</script>



// WEBPACK FOOTER //
// InfiniteMultiselect.vue?6dd5fe2e